import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import collectiveApi from 'modules/common/collectiveApi';
import { useMember } from 'modules/common/queries/member';
import { isInOnboardingStages } from 'modules/common/utils/memberStage';

export type SetupVersion = 'v1' | 'legacy' | '';

export interface OnboardingProgress {
    uuid: string;
    created_at: string;
    updated_at: string;
    is_completed: boolean;
    completed_at: string | null;
    // The flow the member started setup in, stamped when the row is created and never changed. Blank for
    // rows that predate the field, bot traffic, or a failed gate evaluation.
    setup_version?: SetupVersion;
    has_seen_beginning_balance_popup?: boolean;
    bank_connection_status: string;
    transaction_import_status: string;
    home_office_setup_status: string;
    other_document_upload_status: string;
    statement_upload_status?: string;
    connect_automated_statements_status: string;
    bank_connection_data?: {
        linked_accounts: Array<{
            id: string;
            name: string;
            type: string;
            lastFour: string;
            institution: string;
            connected: boolean;
        }>;
        linked_accounts_count: number;
        linked_institutions_count: number;
        confirmed_business_accounts?: Array<{
            id: string;
            name: string;
            type: string;
            mask: string;
            institution_name: string;
            institution_id?: string;
        }>;
        confirmed_personal_accounts?: Array<{
            id: string;
            name: string;
            type: string;
            mask: string;
            institution_name: string;
            institution_id?: string;
        }>;
    };
    transaction_import_data?: {
        source_accounts: Array<{
            id: string;
            name: string;
            type: string;
            lastFour: string;
            institution: string;
            status: string;
            plaidAccountId?: string; // ✅ Add missing field for reconstruction logic
        }>;
    };
    home_office_setup_data?: {
        home_office_expenses: number;
        home_office_percentage: number;
    };
    upload_documents_data?: {
        documents: Array<{
            id: string;
            name: string;
            type: string;
            uploaded_at: string;
        }>;
    };
    other_document_upload_data?: {
        uuid: string;
        document_count: number;
        uploaded_documents: Array<{
            document_type: string;
            member_document_id: string;
            uploaded_at: string;
            file_name: string;
        }>;
        document_types: string[];
        questionnaire_answers?: Record<string, any>;
        skipped_documents?: Record<string, boolean>;
        created_at: string;
        updated_at: string;
    };
    // Manual bank-statement upload step. `uploaded_statements` is the client's record of what it
    // uploaded and which card each file went under. The server's verdict per document is no longer
    // read from here — it has its own member-scoped endpoint, `useStatementUploadStatuses`, because
    // this blob's query is gated on onboarding stage and the dashboard needs the same answer.
    statement_upload_data?: {
        uploaded_statements: Array<{
            member_document_id: string;
            file_name: string;
            uploaded_at: string;
            // Which account card this file was uploaded under, and the only account identity
            // stored here. Not `bank_account_id`: that is null for accounts Tight has assigned
            // no id to, so it cannot tell two of them apart, and the account the backend files
            // against lives on the document itself. Optional — rows written before this field
            // existed simply stop matching a card.
            account_key?: string;
        }>;
        // Accounts the member said they can't supply statements for. A decision, not something
        // derivable, so it must be stored — it re-enables Continue on an incomplete account.
        // Keyed by `account_key`.
        acknowledged_accounts?: string[];
    };
    // Auto Statements onboarding step. Mirrors the backend StatementUploadData shape:
    // tracks which institutions/accounts the member enabled for Quiltt Auto Statements.
    connect_automated_statements_data?: {
        linked_accounts_count: number;
        linked_accounts: Array<{
            id: string;
            name: string;
            type: string;
            mask: string;
            institution_name: string;
            institution_id?: string;
        }>;
        quiltt_connections: Array<{
            connection_id: string;
            institution_id?: string;
            institution_name?: string;
        }>;
    };
}

export const ACCOUNTING_ONBOARDING_PROGRESS_QUERY_KEY = ['accounting-onboarding-progress'];

const fetchOnboardingProgress = async (): Promise<OnboardingProgress> => {
    const response = await collectiveApi.get('/bookkeeping/accounting-onboarding/progress/');
    return response.data;
};

export const useAccountingOnboardingProgress = (
    options?: Omit<UseQueryOptions<OnboardingProgress>, 'queryKey' | 'queryFn'>
) => {
    // ✅ Centralized feature flag check - all callers automatically get this behavior
    const { data: member } = useMember();

    return useQuery({
        queryKey: ACCOUNTING_ONBOARDING_PROGRESS_QUERY_KEY,
        queryFn: async () => {
            const result = await fetchOnboardingProgress();
            return result;
        },
        ...options,
        enabled: Boolean(isInOnboardingStages(member?.stage) && options?.enabled !== false),
    });
};
